import { useCallback, useEffect, useState } from 'react';
import {
  planningAnalyticsApi,
  type AnalyticsAlert,
  type BenchmarkResult,
  type BusinessInsight,
  type DecisionAnalytics,
  type EnterpriseDashboard,
  type KPIDefinition,
  type PlanningAnalyticsSummary,
  type Scorecard,
  type TrendSeries,
} from '../../api/planninganalytics';

type Tab = 'dashboard' | 'kpis' | 'scorecards' | 'trends' | 'benchmarks' | 'decisions' | 'insights' | 'alerts';
type Theme = 'light' | 'dark';

export function PlanningAnalyticsPage() {
  const [theme, setTheme] = useState<Theme>('light');
  const dark = theme === 'dark';
  const [tab, setTab] = useState<Tab>('dashboard');
  const [dashboard, setDashboard] = useState<EnterpriseDashboard | null>(null);
  const [summary, setSummary] = useState<PlanningAnalyticsSummary | null>(null);
  const [kpis, setKpis] = useState<KPIDefinition[]>([]);
  const [scorecards, setScorecards] = useState<Scorecard[]>([]);
  const [trends, setTrends] = useState<TrendSeries[]>([]);
  const [benchmarks, setBenchmarks] = useState<BenchmarkResult[]>([]);
  const [decisions, setDecisions] = useState<DecisionAnalytics[]>([]);
  const [insights, setInsights] = useState<BusinessInsight[]>([]);
  const [alerts, setAlerts] = useState<AnalyticsAlert[]>([]);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setError(null);
    try {
      const [dash, sum, kpiList, sc, tr, bm, dec, ins, al] = await Promise.all([
        planningAnalyticsApi.enterpriseDashboard(),
        planningAnalyticsApi.analyticsSummary(),
        planningAnalyticsApi.listKPIs(),
        planningAnalyticsApi.listScorecards(),
        planningAnalyticsApi.listTrends(),
        planningAnalyticsApi.listBenchmarks(),
        planningAnalyticsApi.listDecisions(),
        planningAnalyticsApi.listInsights(),
        planningAnalyticsApi.listAlerts(),
      ]);
      setDashboard(dash);
      setSummary(sum);
      setKpis(kpiList);
      setScorecards(sc);
      setTrends(tr);
      setBenchmarks(bm);
      setDecisions(dec);
      setInsights(ins);
      setAlerts(al);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load planning analytics');
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const shell = dark ? 'min-h-screen bg-slate-950 text-slate-100' : 'min-h-screen bg-slate-50 text-slate-900';
  const card = dark ? 'rounded-xl border border-slate-800 bg-slate-900 p-5' : 'rounded-xl border border-slate-200 bg-white p-5 shadow-sm';
  const header = dark ? 'border-b border-slate-800 bg-slate-900' : 'border-b border-slate-200 bg-white';
  const muted = dark ? 'text-slate-400' : 'text-slate-600';

  const tabs: { id: Tab; label: string }[] = [
    { id: 'dashboard', label: 'Enterprise Dashboard' },
    { id: 'kpis', label: 'KPI Registry' },
    { id: 'scorecards', label: 'Scorecards' },
    { id: 'trends', label: 'Trend Explorer' },
    { id: 'benchmarks', label: 'Benchmarks' },
    { id: 'decisions', label: 'Decision Intelligence' },
    { id: 'insights', label: 'Insight Center' },
    { id: 'alerts', label: 'Alert Center' },
  ];

  const statusColor = (status: string) => {
    if (status === 'green') return 'bg-emerald-500';
    if (status === 'amber') return 'bg-amber-500';
    return 'bg-red-500';
  };

  return (
    <div className={shell}>
      <div className={`${header} px-6 py-4`}>
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-semibold">Enterprise Planning Analytics</h1>
            <p className={`mt-1 text-sm ${muted}`}>Unified KPI registry, decision intelligence, executive scorecards, and cross-functional business intelligence</p>
          </div>
          <div className="flex gap-2">
            <button type="button" onClick={() => setTheme(dark ? 'light' : 'dark')} className={`rounded-lg px-3 py-2 text-sm ${dark ? 'bg-slate-800' : 'bg-slate-100'}`} aria-label="Toggle dark mode">{dark ? 'Light' : 'Dark'}</button>
            <button type="button" onClick={refresh} className="rounded-lg bg-violet-600 px-4 py-2 text-sm font-medium text-white hover:bg-violet-700">Refresh</button>
          </div>
        </div>
        {dashboard && (
          <p className={`mt-2 text-sm ${muted}`}>
            Intelligence Score {dashboard.enterprise_intelligence_score.toFixed(0)} · {dashboard.registered_kpis} KPIs · {dashboard.active_scorecards} scorecards
          </p>
        )}
      </div>
      {error && <div className="mx-6 mt-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-900 dark:bg-red-950 dark:text-red-300" role="alert">{error}</div>}
      <div className={`${header} px-6`}>
        <nav className="flex gap-1 overflow-x-auto" role="tablist">
          {tabs.map((t) => (
            <button key={t.id} type="button" role="tab" aria-selected={tab === t.id} onClick={() => setTab(t.id)}
              className={`whitespace-nowrap border-b-2 px-4 py-3 text-sm font-medium ${tab === t.id ? 'border-violet-600 text-violet-600' : 'border-transparent text-slate-500'}`}>{t.label}</button>
          ))}
        </nav>
      </div>
      <div className="p-6">
        {tab === 'dashboard' && dashboard && (
          <>
            <div className="mb-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {[
                { label: 'Enterprise Intelligence', value: dashboard.enterprise_intelligence_score.toFixed(0) },
                { label: 'Enterprise Confidence', value: dashboard.enterprise_confidence_score.toFixed(0) },
                { label: 'Planning Health Index', value: dashboard.planning_health_index.toFixed(0) },
                { label: 'Decision Quality Index', value: dashboard.decision_quality_index.toFixed(0) },
                { label: 'Enterprise Readiness', value: `${dashboard.enterprise_readiness.toFixed(0)}%` },
                { label: 'Forecast Accuracy', value: `${dashboard.forecast_accuracy.toFixed(1)}%` },
                { label: 'Planning Accuracy', value: `${dashboard.planning_accuracy.toFixed(1)}%` },
                { label: 'Consensus Score', value: `${dashboard.consensus_score.toFixed(0)}%` },
                { label: 'Inventory Health', value: dashboard.inventory_health.toFixed(0) },
                { label: 'Supplier Reliability', value: `${dashboard.supplier_reliability.toFixed(0)}%` },
                { label: 'Open Alerts', value: dashboard.open_alerts },
                { label: 'Pending Insights', value: dashboard.pending_insights },
              ].map((m) => (
                <div key={m.label} className={card}><p className={`text-sm ${muted}`}>{m.label}</p><p className="mt-1 text-2xl font-semibold">{m.value}</p></div>
              ))}
            </div>
            {summary && (
              <div className={card}>
                <h3 className="font-medium">Domain Health</h3>
                <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                  {summary.domains.map((d) => (
                    <div key={d.domain} className="flex items-center gap-3">
                      <span className={`h-3 w-3 rounded-full ${statusColor(d.health_status)}`} />
                      <span className="text-sm capitalize">{d.domain.replace(/_/g, ' ')}</span>
                      <span className={`ml-auto text-sm ${muted}`}>{d.avg_score.toFixed(0)}% · {d.kpi_count} KPIs</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
        {tab === 'kpis' && kpis.map((k) => (
          <div key={k.kpi_id} className={`${card} mb-4`}>
            <div className="flex items-center justify-between">
              <h3 className="font-medium">{k.business_name}</h3>
              <span className={`text-xs ${muted}`}>{k.kpi_id}</span>
            </div>
            <p className={`mt-1 text-sm ${muted}`}>{k.domain} · {k.owner} · {k.refresh_frequency}</p>
            <p className="mt-2 text-sm">{k.description}</p>
            <div className="mt-2 flex gap-4">
              <span className="text-lg font-semibold">{k.current_value.toFixed(1)} {k.units}</span>
              <span className={`text-sm ${muted}`}>Target: {k.target_value.toFixed(1)}</span>
              {k.variance_pct != null && <span className={`text-sm ${k.variance_pct < 0 ? 'text-red-500' : 'text-emerald-500'}`}>{k.variance_pct.toFixed(1)}% variance</span>}
            </div>
          </div>
        ))}
        {tab === 'scorecards' && scorecards.map((sc) => (
          <div key={sc.id} className={`${card} mb-4`}>
            <div className="flex items-center justify-between">
              <h3 className="font-medium capitalize">{sc.scorecard_type.replace(/_/g, ' ')} Scorecard</h3>
              <span className="text-xl font-semibold">{sc.overall_score.toFixed(0)}</span>
            </div>
            <div className="mt-2 flex gap-4 text-sm">
              <span className="text-emerald-600">{sc.green_count} green</span>
              <span className="text-amber-600">{sc.amber_count} amber</span>
              <span className="text-red-600">{sc.red_count} red</span>
              <span className={muted}>{sc.kpi_count} KPIs</span>
            </div>
          </div>
        ))}
        {tab === 'trends' && trends.map((tr) => (
          <div key={tr.id} className={`${card} mb-4`}>
            <h3 className="font-medium">{tr.kpi_name}</h3>
            <p className={`mt-1 text-sm ${muted}`}>{tr.frequency} · MA {tr.moving_avg?.toFixed(1)}</p>
            <div className="mt-3 flex gap-2">
              {tr.points.map((p) => (
                <div key={p.period} className="flex-1 text-center">
                  <div className="mx-auto h-16 w-full max-w-[40px] rounded bg-violet-100 dark:bg-violet-900" style={{ height: `${Math.min(p.value, 100)}px` }} />
                  <p className={`mt-1 text-xs ${muted}`}>{p.period.slice(-3)}</p>
                  <p className="text-xs font-medium">{p.value.toFixed(0)}</p>
                </div>
              ))}
            </div>
          </div>
        ))}
        {tab === 'benchmarks' && benchmarks.map((b) => (
          <div key={b.id} className={`${card} mb-4 flex items-center justify-between`}>
            <div>
              <h3 className="font-medium">#{b.rank} {b.entity_name}</h3>
              <p className={`text-sm ${muted}`}>{b.percentile.toFixed(0)}th percentile · avg {b.enterprise_avg.toFixed(1)}</p>
            </div>
            <span className={`text-lg font-semibold ${b.gap_pct >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>{b.value.toFixed(1)}</span>
          </div>
        ))}
        {tab === 'decisions' && decisions.map((d) => (
          <div key={d.id} className={`${card} mb-4`}>
            <div className="flex items-center justify-between">
              <h3 className="font-medium">{d.title}</h3>
              <span className={`rounded-full px-2 py-0.5 text-xs ${d.decision_outcome === 'approved' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>{d.decision_outcome}</span>
            </div>
            <p className={`mt-1 text-sm ${muted}`}>{d.decision_owner} · {d.module_source} · accuracy {d.decision_accuracy}%</p>
            <p className={`mt-1 text-sm ${muted}`}>Benefit ${d.decision_benefit.toLocaleString()} · Cost ${d.decision_cost.toLocaleString()}</p>
          </div>
        ))}
        {tab === 'insights' && insights.map((i) => (
          <div key={i.id} className={`${card} mb-4`}>
            <h3 className="font-medium">{i.title}</h3>
            <p className={`mt-1 text-sm ${muted}`}>{i.type.replace(/_/g, ' ')} · Confidence {(i.confidence * 100).toFixed(0)}%</p>
            <p className="mt-2 text-sm">{i.summary}</p>
            <p className={`mt-1 text-sm ${muted}`}>{i.business_explanation}</p>
          </div>
        ))}
        {tab === 'alerts' && alerts.map((a) => (
          <div key={a.id} className={`${card} mb-4`}>
            <div className="flex items-center justify-between">
              <h3 className="font-medium">{a.title}</h3>
              <span className={`text-sm font-medium ${a.severity === 'critical' ? 'text-red-600' : 'text-amber-600'}`}>{a.severity}</span>
            </div>
            <p className={`mt-1 text-sm ${muted}`}>{a.alert_type.replace(/_/g, ' ')}</p>
            <p className="mt-2 text-sm">{a.description}</p>
          </div>
        ))}
      </div>
    </div>
  );
}
